import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { Button, GDSButtonProps } from '#components/button/button.tsx';
import {
  ProgressBar,
  GDSProgressBarProps,
} from '#components/progress-bar/progress-bar.tsx';
import { HeadingText } from '#components/typography/heading-text/heading-text.tsx';
import { Box } from '#components/box/box.tsx';
import { GDSCustomizableComponent } from '#src/types.ts';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import { styled } from '../../theme';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

const DISPLAY_NAME = 'FlowNavigation';

export interface GDSFlowNavigationProps extends GDSCustomizableComponent {
  'data-testid'?: string;
  accessibilityLabel?: string;
  backgroundColor?: 'primary' | 'secondary' | 'transparent';
  divider?: 'top' | 'bottom' | 'none';
  label?: string;
  progressBar?: GDSProgressBarProps;
  size?: 'compact' | 'standard';
  leadingButton?: Omit<GDSButtonProps<'button' | 'a'>, 'children'> & {
    label: string;
  };
  trailingButton?: Omit<GDSButtonProps<'button' | 'a'>, 'children'> & {
    label: string;
  };
  contentMaxWidth?: string;
  /**
   * When `false` or omitted, the flow navigation applies its default `tabIndex`
   * (typically to make the container focusable as part of the keyboard tab order).
   *
   * Set this to `true` to prevent the component from assigning its default
   * `tabIndex`, allowing the consuming application to control focusability and
   * tab order on the navigation container (e.g. via a custom `tabIndex` or by
   * leaving it unfocusable).
   */
  disableFocus?: boolean;
}

const ContentWrapper = ({
  children,
  contentMaxWidth,
}: {
  children: ReactNode;
  contentMaxWidth?: string;
}): JSX.Element =>
  contentMaxWidth ? (
    <Box
      css={{
        width: '100%',
        maxWidth: contentMaxWidth,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </Box>
  ) : (
    (children as JSX.Element)
  );

const FlowNavigationContainer = styled('nav', {
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr max-content',
  alignItems: 'center',
  gap: '$one',
  '&[class*="GDS"]:focus, &[class*="GDS"]:focus-visible': {
    boxShadow: 'none',
    outline: 'none',
  },
  variants: {
    backgroundColor: {
      primary: {
        backgroundColor: '$surfaceBackgroundPrimary',
      },
      secondary: {
        backgroundColor: '$surfaceBackgroundSecondary',
      },
      transparent: {
        backgroundColor: 'transparent',
      },
    },
    size: {
      compact: {
        padding: '$half $none',
      },
      standard: {
        padding: '$one $none',
      },
    },
    divider: {
      top: {
        borderBlockStart: '$borderWidths$thin solid $onSurfaceBorderSubdued',
      },
      bottom: {
        borderBlockEnd: '$borderWidths$thin solid $onSurfaceBorderSubdued',
      },
      none: {
        border: 'none',
      },
    },
  },
});

const ButtonContainer = styled('div', {
  display: 'flex',
  variants: {
    position: {
      leading: {
        justifyContent: 'flex-start',
      },
      trailing: {
        justifyContent: 'flex-end',
      },
    },
    reserveSpace: {
      true: {
        minWidth: 'var(--button-width)',
      },
      false: {},
    },
  },
});

export const FlowNavigation = forwardRef<HTMLElement, GDSFlowNavigationProps>(
  (
    {
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      accessibilityLabel,
      backgroundColor = 'transparent',
      className,
      css,
      divider,
      label,
      leadingButton,
      progressBar,
      size = 'standard',
      trailingButton,
      contentMaxWidth,
      disableFocus = false,
      ...props
    },
    ref,
  ) => {
    const internalLeadingButtonRef = useRef<
      HTMLButtonElement | HTMLAnchorElement
    >(null);
    const internalTrailingButtonRef = useRef<
      HTMLButtonElement | HTMLAnchorElement
    >(null);
    const [buttonWidth, setButtonWidth] = useState<string>('auto');

    const {
      label: leadingButtonLabel,
      priority: leadingButtonPriority = 'secondary',
      textAlign: leadingButtonTextAlign,
      ref: externalLeadingButtonRef,
      size: leadingButtonSize = 'toolbar',
      ...leadingButtonProps
    } = leadingButton || {};
    const {
      label: trailingButtonLabel,
      priority: trailingButtonPriority = 'primary',
      textAlign: trailingButtonTextAlign,
      ref: externalTrailingButtonRef,
      size: trailingButtonSize = 'toolbar',
      ...trailingButtonProps
    } = trailingButton || {};

    const { height: progressBarHeight = 'medium' } = progressBar || {};

    // Compose internal and external refs
    const composedLeadingButtonRef = useComposedRefs(
      internalLeadingButtonRef,
      externalLeadingButtonRef,
    );
    const composedTrailingButtonRef = useComposedRefs(
      internalTrailingButtonRef,
      externalTrailingButtonRef,
    );

    useEffect(() => {
      // Measure the width of existing buttons to reserve space for missing ones
      const leadingWidth = internalLeadingButtonRef.current?.offsetWidth || 0;
      const trailingWidth = internalTrailingButtonRef.current?.offsetWidth || 0;
      const maxWidth = Math.max(leadingWidth, trailingWidth);

      if (maxWidth > 0) {
        setButtonWidth(`${maxWidth}px`);
      }
    }, [
      leadingButton,
      trailingButton,
      leadingButtonLabel,
      trailingButtonLabel,
    ]);

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <FlowNavigationContainer
          aria-label={accessibilityLabel}
          backgroundColor={backgroundColor}
          className={['GDS-flow-navigation', className].join(' ')}
          css={{
            '--button-width': buttonWidth,
            ...css,
          }}
          data-testid={testId}
          divider={divider}
          ref={ref}
          size={size}
          tabIndex={disableFocus ? undefined : -1}
          {...props}
        >
          <ButtonContainer
            position="leading"
            reserveSpace={!leadingButton && !!trailingButton}
          >
            {leadingButton && (
              <Button
                ref={composedLeadingButtonRef}
                className="GDS-flow-navigation-leading-button"
                priority={leadingButtonPriority}
                textAlign={leadingButtonTextAlign}
                size={leadingButtonSize}
                {...leadingButtonProps}
              >
                {leadingButtonLabel}
              </Button>
            )}
          </ButtonContainer>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-live="polite"
          >
            <ContentWrapper contentMaxWidth={contentMaxWidth}>
              {progressBar ? (
                <ProgressBar
                  className="GDS-flow-navigation-progress-bar"
                  height={progressBarHeight}
                  css={{
                    minWidth: '64px',
                  }}
                  {...progressBar}
                />
              ) : (
                <HeadingText
                  level="2"
                  size="xs"
                  className="GDS-flow-navigation-label"
                >
                  {label}
                </HeadingText>
              )}
            </ContentWrapper>
          </Box>
          <ButtonContainer
            position="trailing"
            reserveSpace={!trailingButton && !!leadingButton}
          >
            {trailingButton && (
              <Button
                ref={composedTrailingButtonRef}
                priority={trailingButtonPriority}
                className="GDS-flow-navigation-trailing-button"
                textAlign={trailingButtonTextAlign}
                size={trailingButtonSize}
                {...trailingButtonProps}
              >
                {trailingButtonLabel}
              </Button>
            )}
          </ButtonContainer>
        </FlowNavigationContainer>
      </GenesisCoreInspector>
    );
  },
);

FlowNavigation.displayName = DISPLAY_NAME;
